import { Component, DestroyRef, Inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { InteractionType } from '@azure/msal-browser';
import { GoogleSigninButtonDirective } from '@abacritt/angularx-social-login';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ModelDetails, ORGANISATION_NAME_MAX, ORGANISATION_NAME_MIN } from '../../common/organisation';
import { EMAIL_MAX, isValidEmail, PASSWORD_MAX, PASSWORD_MIN, USER_NAME_MAX, USER_NAME_MIN } from '../../common/user';
import { getUserMessage, LinqException } from '../../common/exceptions';
import { MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { UserService } from '../user.service';
import { RepositoryService } from '../../linq-utils/repository.service';

// a name of spaces would pass the length checks
const trimmedMin = (min:number) => (control:AbstractControl):ValidationErrors | null =>
    control.value && control.value.trim().length < min ? {minlength:true} : null

const validEmail = (control:AbstractControl):ValidationErrors | null =>
    !control.value || isValidEmail(control.value.trim()) ? null : {email:true}

// registers a new user and organisation, optionally copying the model they tried. Stays open until the server accepts it,
// so a problem such as an email already in use can be fixed without typing everything again. Closes with the email once registered
@Component({
    templateUrl: './register-dialog.html',
    styleUrls: ['./register-dialog.scss'],
    standalone: false,
    providers: [GoogleSigninButtonDirective]
})
export class RegisterDialogComponent {
    registrationForm: FormGroup
    registering = false
    errorMessage = ''
    showPassword = false

    ORGANISATION_NAME_MIN = ORGANISATION_NAME_MIN
    ORGANISATION_NAME_MAX = ORGANISATION_NAME_MAX
    USER_NAME_MIN = USER_NAME_MIN
    USER_NAME_MAX = USER_NAME_MAX
    PASSWORD_MIN = PASSWORD_MIN
    PASSWORD_MAX = PASSWORD_MAX
    EMAIL_MAX = EMAIL_MAX

    constructor(public dialogRef: MatDialogRef<RegisterDialogComponent, string>, private formBuilder: FormBuilder, private userService:UserService,
        private repoService:RepositoryService, @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration, private authService: MsalService,
        @Inject(MAT_DIALOG_DATA) public data:{model:ModelDetails, messages:string[]}, destroyRef:DestroyRef) {

        this.registrationForm = this.formBuilder.group({
            organisation: ['', [Validators.required, Validators.maxLength(ORGANISATION_NAME_MAX), trimmedMin(ORGANISATION_NAME_MIN)]],
            usersname: ['', [Validators.required, Validators.maxLength(USER_NAME_MAX), trimmedMin(USER_NAME_MIN)]],
            email: ['', [Validators.required, validEmail]],
            password: ['', [Validators.required, Validators.minLength(PASSWORD_MIN), Validators.maxLength(PASSWORD_MAX)]],
            passwordConfirmation: ['', [Validators.required]],
            createModel: !!data.model,
        }, {validators: this.passwordsMatch})

        // signing in with Microsoft or Google registers too, using the organisation name and model kept here
        this.userService.organisationName = ""
        this.userService.modelKey = data.model?._key
        const {organisation, createModel} = this.registrationForm.controls
        organisation.valueChanges.pipe(takeUntilDestroyed(destroyRef)).subscribe(name => this.userService.organisationName = (name || '').trim())
        createModel.valueChanges.pipe(takeUntilDestroyed(destroyRef)).subscribe(create => this.userService.modelKey = create ? this.data.model?._key : undefined)
        // a new attempt clears the last problem
        this.registrationForm.valueChanges.pipe(takeUntilDestroyed(destroyRef)).subscribe(() => this.errorMessage = '')
    }

    private passwordsMatch = (group:AbstractControl):ValidationErrors | null => {
        const password = group.get('password')?.value, confirmation = group.get('passwordConfirmation')?.value
        return confirmation && password !== confirmation ? {passwordMismatch:true} : null
    }

    passwordsDiffer() {
        return this.registrationForm.hasError('passwordMismatch') && this.registrationForm.controls['passwordConfirmation'].touched
    }

    canUseSocialSignIn() {
        return this.registrationForm.controls['organisation'].valid
    }

    async register() {
        this.registrationForm.markAllAsTouched()
        if (this.registrationForm.invalid || this.registering) return

        const v = this.registrationForm.value
        const request = {
            organisation: v.organisation.trim(),
            usersname: v.usersname.trim(),
            email: v.email.trim(),
            password: v.password,
            passwordConfirmation: v.passwordConfirmation,
            createModel: v.createModel,
            modelKey: this.data.model?._key,
        }

        this.registering = true
        try {
            await this.repoService.callSingleAPI(this.repoService.create('api/public/register', request), "Register dialog", "Register", undefined, undefined, undefined, undefined, false)
            this.dialogRef.close(request.email)
        }catch(err) {
            this.errorMessage = getUserMessage(err as LinqException) || "Registration didn't work. Please try again."
        }finally {
            this.registering = false
        }
    }

    loginMicrosoft() {
        if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
            this.authService.loginPopup()
        } else {
            this.authService.loginRedirect();
        }
        this.dialogRef.close()
    }
}
